import React, { useEffect, useRef } from 'react'

import classNames from 'classnames'
import { createPortal } from 'react-dom'

import './Modal.scss'
import { ModalContext } from './context'
import { Body } from './parts/Body'
import { Footer } from './parts/Footer'
import { Header } from './parts/Header'

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  variant?: 'default' | 'danger'
  closeOnOutsideClick?: boolean
  className?: string
  style?: React.CSSProperties
  disablePortal?: boolean
}

const ModalRoot: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  variant = 'default',
  closeOnOutsideClick = true,
  className,
  style,
  disablePortal = false,
}) => {
  const contentRef = useRef<HTMLDivElement>(null)

  // Local outside-click handling specific to Modal
  useEffect(() => {
    if (!isOpen || !closeOnOutsideClick) return

    const handleDown = (e: MouseEvent) => {
      const node = contentRef.current
      if (!node) return
      if (node.contains(e.target as Node)) return

      // Check if click is within a react-select portal menu
      const target = e.target as Element
      if (target.closest('[class*="__menu"], [class*="-menu"]')) return

      onClose()
    }

    // Defer binding to avoid catching the opening interaction
    const id = setTimeout(() => {
      document.addEventListener('mousedown', handleDown, true)
    }, 0)

    return () => {
      clearTimeout(id)
      document.removeEventListener('mousedown', handleDown, true)
    }
  }, [isOpen, closeOnOutsideClick, onClose])

  if (!isOpen) return null

  const modalContent = (
    <ModalContext.Provider value={{ onClose, variant }}>
      <div className={classNames('o-modal-backdrop', 'open', variant)} />
      <div
        className={classNames('o-modal-root', variant, className)}
        style={style}
      >
        <div
          className="o-modal-content"
          ref={contentRef}
          role="dialog"
          aria-modal="true"
        >
          {children}
        </div>
      </div>
    </ModalContext.Provider>
  )

  if (disablePortal) return modalContent

  return createPortal(modalContent, document.body)
}

export const Modal = Object.assign(ModalRoot, {
  Header,
  Body,
  Footer,
})
